/**
 * Slice 2 — Shadow re-run with an assumption negated.
 * Never persists placements; returns launch / team / $ deltas vs baseline.
 */

import {
  impactMagnitude,
  summarizeImpact,
  type AssumptionImpact,
  type TimePhasedAssumption,
} from './timePhasedAssumptions';
import { runTimePhasedPlacementEngine } from './timePhasedPlanningEngine';
import type {
  TimePhasedEngineInput,
  TimePhasedEngineOutput,
  TimePhasedPriorityInput,
} from './timePhasedPlanning.types';
import type { FundingAffordability } from './timePhasedFinancial.types';

export interface ShadowImpactResult {
  assumptionId: string;
  impact: AssumptionImpact;
  computedAt: string;
}

function addDays(iso: string, days: number): string {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function weeksBetween(a: string, b: string): number {
  const ms = new Date(b + 'T00:00:00Z').getTime() - new Date(a + 'T00:00:00Z').getTime();
  return Math.round(ms / (7 * 24 * 60 * 60 * 1000));
}

/** Negate the assumption in a copy of the engine input (pure). */
export function negateAssumptionInInput(
  input: TimePhasedEngineInput,
  assumption: TimePhasedAssumption,
): TimePhasedEngineInput {
  const priorities = input.priorities.map((p) => ({ ...p }));
  let supplyCalendar = input.supplyCalendar.map((r) => ({ ...r }));

  switch (assumption.kind) {
    case 'resource-allocation':
    case 'capacity': {
      const rt = assumption.subject || assumption.refs?.resourceType;
      if (rt) {
        supplyCalendar = supplyCalendar.map((row) =>
          row.resourceType === rt || row.resourceType.toLowerCase() === rt.toLowerCase()
            ? { ...row, supplyHours: 0 }
            : row,
        );
      }
      break;
    }
    case 'date-hold': {
      const pid =
        assumption.refs?.priorityId ??
        assumption.derived_from.find((d) => d.startsWith('placement:'))?.slice('placement:'.length);
      if (pid) {
        const idx = priorities.findIndex((p) => p.id === pid);
        if (idx >= 0) {
          const p = priorities[idx];
          // Soft date treated as available → negate by slipping target 6 weeks
          priorities[idx] = {
            ...p,
            targetLaunchStart: p.targetLaunchStart
              ? addDays(p.targetLaunchStart, 42)
              : p.targetLaunchStart,
            targetLaunchEnd: p.targetLaunchEnd ? addDays(p.targetLaunchEnd, 42) : p.targetLaunchEnd,
            // If it was fixed, unlock so the shadow can show what moves when the hold fails
            launchFlexibility: 'flexible',
          };
        }
      }
      break;
    }
    case 'dependency-delivery': {
      const pid =
        assumption.refs?.priorityId ??
        assumption.derived_from.find((d) => d.startsWith('placement:'))?.slice('placement:'.length);
      if (pid) {
        const idx = priorities.findIndex((p) => p.id === pid);
        if (idx >= 0) {
          priorities[idx] = {
            ...priorities[idx],
            viabilityIncluded: false,
          } as TimePhasedPriorityInput;
        }
      }
      break;
    }
    case 'funding': {
      // Funding negation is compared via affordability caller; placement shadow zeros cost-bearing effort signal
      break;
    }
    default:
      break;
  }

  return { ...input, priorities, supplyCalendar };
}

function diffPlacements(
  baseline: TimePhasedEngineOutput,
  shadow: TimePhasedEngineOutput,
): AssumptionImpact['launchesAffected'] {
  const baseById = new Map(baseline.placements.map((p) => [p.priorityId, p]));
  const out: AssumptionImpact['launchesAffected'] = [];
  for (const s of shadow.placements) {
    const b = baseById.get(s.priorityId);
    if (!b) continue;
    const oldDate = b.feasibleStart ?? b.targetLaunchStart;
    const newDate = s.feasibleStart ?? s.targetLaunchStart;
    const slipped =
      (oldDate && newDate && newDate > oldDate) ||
      (s.slipWeeks ?? 0) > (b.slipWeeks ?? 0) ||
      (b.placementStatus === 'scheduled' && s.placementStatus !== 'scheduled');
    if (!slipped && s.placementStatus === b.placementStatus) continue;

    let effect: AssumptionImpact['launchesAffected'][0]['effect'] = 'slips';
    if (s.placementStatus === 'blocked') effect = 'blocked';
    else if (s.placementStatus === 'supply_exhausted') effect = 'supply_exhausted';
    else if (!s.launchable && b.launchable) effect = 'unlaunchable';

    const slipWeeks =
      oldDate && newDate && newDate > oldDate
        ? weeksBetween(oldDate, newDate)
        : Math.max(0, (s.slipWeeks ?? 0) - (b.slipWeeks ?? 0));

    out.push({
      priorityId: s.priorityId,
      priorityName: s.priorityName,
      effect,
      slipWeeks: slipWeeks || undefined,
      oldDate: oldDate ?? undefined,
      newDate: newDate ?? undefined,
    });
  }
  return out;
}

function diffTeams(
  baseline: TimePhasedEngineOutput,
  shadow: TimePhasedEngineOutput,
): AssumptionImpact['teamsOverload'] {
  const baseKey = new Map(
    (baseline.bucketUtilization ?? []).map((b) => [`${b.resourceType}::${b.bucketStart}`, b]),
  );
  const out: AssumptionImpact['teamsOverload'] = [];
  for (const b of shadow.bucketUtilization ?? []) {
    const overage = Math.max(0, b.demandHours - b.supplyHours);
    if (b.utilizationPct < 85 && overage <= 0) continue;
    const prev = baseKey.get(`${b.resourceType}::${b.bucketStart}`);
    const prevOverage = prev ? Math.max(0, prev.demandHours - prev.supplyHours) : 0;
    const worsened =
      !prev ||
      b.utilizationPct > (prev.utilizationPct ?? 0) + 5 ||
      overage > prevOverage;
    if (!worsened && b.utilizationPct < 100) continue;
    out.push({
      resourceType: b.resourceType,
      bucketStart: b.bucketStart,
      utilizationPct: b.utilizationPct,
      overageHours: overage,
    });
  }
  return out.slice(0, 12);
}

function fundingBreaksFrom(
  financial?: { affordability: FundingAffordability | null } | null,
): AssumptionImpact['fundingBreaks'] {
  const aff = financial?.affordability;
  if (!aff?.overloadedBuckets?.length) return [];
  return aff.overloadedBuckets.map((b) => ({
    bucketStart: b.bucketStart,
    overage: b.overage,
  }));
}

/**
 * Run a shadow placement with the assumption negated and diff vs baseline.
 * Pure / in-memory — caller must not persist shadow.placements.
 */
export function computeAssumptionShadowImpact(args: {
  assumption: TimePhasedAssumption;
  baselineInput: TimePhasedEngineInput;
  baselineOutput: TimePhasedEngineOutput;
  /** Optional: when funding assumption, pass affordability after a hypothetical envelope shrink. */
  shadowFinancial?: { affordability: FundingAffordability | null } | null;
}): ShadowImpactResult {
  const { assumption, baselineInput, baselineOutput, shadowFinancial } = args;

  if (assumption.kind === 'funding') {
    const breaks = fundingBreaksFrom(shadowFinancial) ;
    // If no alternate affordability, invent pressure from baseline overage * 1.5 or synthetic
    const fundingBreaks =
      breaks.length > 0
        ? breaks
        : [
            {
              bucketStart: assumption.windowStart ?? baselineInput.horizonStart,
              overage: Math.max(40_000, (assumption.quantity ?? 0) * 0.1),
            },
          ];
    const impact: AssumptionImpact = {
      launchesAffected: (baselineOutput.placements ?? [])
        .filter((p) => p.launchable || p.placementStatus === 'scheduled')
        .slice(0, 8)
        .map((p) => ({
          priorityId: p.priorityId,
          priorityName: p.priorityName,
          effect: 'confidenceconfidence' as const,
          oldDate: p.feasibleStart,
          newDate: p.feasibleStart,
        })),
      teamsOverload: [],
      fundingBreaks,
    };
    impact.magnitude = impactMagnitude(impact);
    impact.summary = summarizeImpact(impact);
    return { assumptionId: assumption.id, impact, computedAt: new Date().toISOString() };
  }

  const negated = negateAssumptionInInput(baselineInput, assumption);
  const shadow = runTimePhasedPlacementEngine(negated);
  const impact: AssumptionImpact = {
    launchesAffected: diffPlacements(baselineOutput, shadow),
    teamsOverload: diffTeams(baselineOutput, shadow),
    fundingBreaks: fundingBreaksFrom(shadowFinancial),
  };
  impact.magnitude = impactMagnitude(impact);
  impact.summary = summarizeImpact(impact);
  return { assumptionId: assumption.id, impact, computedAt: new Date().toISOString() };
}

/** In-memory cache keyed by runKey + assumptionId. */
const shadowCache = new Map<string, ShadowImpactResult>();

export function getCachedShadowImpact(runKey: string, assumptionId: string): ShadowImpactResult | null {
  return shadowCache.get(`${runKey}::${assumptionId}`) ?? null;
}

export function setCachedShadowImpact(runKey: string, result: ShadowImpactResult): void {
  shadowCache.set(`${runKey}::${result.assumptionId}`, result);
}

export function invalidateShadowImpactCache(runKey?: string): void {
  if (!runKey) {
    shadowCache.clear();
    return;
  }
  for (const k of [...shadowCache.keys()]) {
    if (k.startsWith(`${runKey}::`)) shadowCache.delete(k);
  }
}
